import React, { useState, useEffect, useCallback } from "react";
import StatusBadge from "./StatusBadge";
import RequestDetailsModal from "./RequestDetailsModal";
import DataFallback from './feedback/DataFallback';

const RequestHistory = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Fetch service requests from the new API endpoint
      const serviceResponse = await fetch(`${API_BASE}/api/ssn/my-requests/`, { headers });
              
      let serviceRequests = [];
      if (serviceResponse.ok) {
        const serviceData = await serviceResponse.json();
        serviceRequests = serviceData.requests || [];
      }
              
      // Try to fetch concierge requests (using the correct endpoint)
      const conciergeResponse = await fetch(`${API_BASE}/api/ssn/my-requests/`, { headers });
      
      let conciergeRequests = [];
      if (conciergeResponse.ok) {
        const conciergeData = await conciergeResponse.json();
        // Handle both array and single object responses
        conciergeRequests = Array.isArray(conciergeData) ? conciergeData : [conciergeData];
      }

      // Combine both types of requests
      const allRequests = [
        ...serviceRequests.map(req => ({ ...req, type: 'Service Request' })),
        ...conciergeRequests.map(req => ({ ...req, type: 'Concierge Request' }))
      ];

      // Sort by date (most recent first)
      allRequests.sort((a, b) => new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at));

      setRequests(allRequests);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests. You may not have submitted any requests yet.');
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', margin: '20px' }}>
        Loading requests...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', margin: '20px' }}>
        <DataFallback
          variant="card"
          error={typeof error === 'string' ? { message: error, retryable: true } : error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '20px',
      margin: '20px 0'
    }}>
      <h2 style={{
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '20px',
        color: '#1a3c34'
      }}>
        Request History
      </h2>

      {requests.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📋</div>
          <h3>No requests found</h3>
          <p>Submit service requests through vendor pages or concierge requests using the form above</p>
          <p style={{ marginTop: '10px', fontSize: '14px' }}>
            All your requests will appear here once submitted
          </p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <thead style={{
              backgroundColor: '#f8fafc',
              fontWeight: '600'
            }}>
              <tr>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  Date
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  Type
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  Details
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  Status
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request, index) => (
                <tr 
                  key={request.id || index}
                  style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                    ':hover': {
                      backgroundColor: '#f1f5f9'
                    }
                  }}
                >
                  <td style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    {formatDate(request.created_at || request.updated_at)}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    {request.type}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    {request.type === 'Service Request' ? (
                      <div>
                        <div><strong>{request.vendor_name || 'Unknown Vendor'}</strong></div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {request.service_name || 'Service Request'}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div><strong>{request.service_name || 'Concierge Request'}</strong></div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Concierge
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <StatusBadge status={request.status} />
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <button
                      style={{
                        backgroundColor: '#2a9d8f',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onClick={() => setSelectedRequest(request)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Request Details Modal */}
      <RequestDetailsModal 
        request={selectedRequest} 
        onClose={() => setSelectedRequest(null)} 
      />
    </div>
  );
};

export default RequestHistory;